import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { ScopeTenant } from '@app/models/scope-tenant';
import { ScopeInstance } from '@models/scope-instance';
import { ScopeServer } from '@models/scope-server';

import { Observable, catchError, map, of, shareReplay, take, tap } from 'rxjs';
import { REGISTRAR_API } from '../app.token';
import { LocalStorageService } from './local-storage.service';

/**
 * registrar service handles api request which are related with scope clients
 */
@Injectable( {
    providedIn: 'root'
} )
export class RegistrarService {
    #apiUrl = inject( REGISTRAR_API );
    #httpClient = inject( HttpClient );
    #localStorageService = inject( LocalStorageService );

    readonly #tenantId = signal( '' );
    readonly #scopeTenant = signal<ScopeTenant | null>( null );
    #pending$: Observable<ScopeTenant> | null = null;

    /** scope tenant organization label: `${organizationName} (${scopeDomain})` */
    readonly organizationLabel = computed( () => {
        const tenant = this.#scopeTenant();
        if ( !tenant?.organizationName ) return '';
        return tenant.scopeDomain
            ? `${tenant.organizationName} (${tenant.scopeDomain})`
            : tenant.organizationName;
    } );

    /**
     * get server for a Scope tenant
     * @param       scopeTenantID Scope tenant ID
     * @returns     scope servers
     */
    getScopeServer( scopeTenantID: string ): Observable<ScopeServer[]> {
        const dto = {
            scopeTenantID,
        };
        const params = new URLSearchParams( dto );

        return this.#httpClient.get<ScopeServer[]>( `${this.#apiUrl}/scopeInstallations?${params}` )
            .pipe(
                take( 1 ),
                tap( ( scopeServers: ScopeServer[] ) => {
                    if ( scopeServers.length ) {
                        this.#localStorageService.save( 'servers', scopeServers );
                    }
                } ),
                map( ( scopeServers: ScopeServer[] ) => scopeServers ),
                catchError( ( err: any ) => {
                    return of( err );
                } ) );
    }

    /**
     * get list of scope instances
     * @returns    json object with list of scope instances
     */
    getScopeInstances(): Observable<ScopeInstance[]> {
        return this.#httpClient.get<ScopeInstance[]>( `${this.#apiUrl}/domains` ).pipe( ( instances ) => instances );
    }

    /**
     * get scope instance by id
     * @param id            id of scope instance
     * @returns {object}    json object with instance details
     */
    getScopeInstanceById( id: string ): Observable<ScopeInstance> {
        return this.#httpClient.get<ScopeInstance>( `${this.#apiUrl}/domains/${id}` )
            .pipe(
                take( 1 ),
                map( ( instance ) => instance ),
                catchError( ( err: any ) => {
                    return of( err );
                } )
            );
    }

    /**
     * get server details from registrar
     * and sort names of organizations depending on user language
     * @param instance  name of scope instance
     * @returns {Array} list of scope instance details
     */
    getScopeInstanceDetails( instance: string ) {
        const account = this.#localStorageService.load( 'account' );
        if ( account ) {
            return this.#httpClient.get<ScopeInstance>( `${this.#apiUrl}/domains/${instance}` ).pipe(
                map( ( scopeDomain: ScopeInstance ) => scopeDomain ),
                catchError( ( err: any ) => {
                    return of( err );
                } )
            );
        } else {
            console.log( 'Session expired' );
            return of( null );
        }
    }

    /**
     * Retrieve concrete Scope Tenant data for a given id
     * @param tenantID
     * @returns Observable<ScopeTenant> - in case of error empty ScopeTenant is emitted
     */
    getScopeTenantByID( tenantID: string ): Observable<ScopeTenant> {
        if ( !tenantID ) return of( {} as ScopeTenant );

        const isSameTenant = this.#tenantId() === tenantID;

        // value cache
        if ( isSameTenant && this.#scopeTenant() ) return of( this.#scopeTenant()! );
        // in-flight request -> second consumer joins, no second call
        if ( isSameTenant && this.#pending$ ) return this.#pending$;

        this.#tenantId.set( tenantID );
        this.#scopeTenant.set( null );

        this.#pending$ = this.#httpClient
            .get<ScopeTenant>( `${this.#apiUrl}/scopetenants/${tenantID}` )
            .pipe(
                take( 1 ),
                tap( tenant => this.#scopeTenant.set( tenant ) ),
                catchError( ( err: any ) => {
                    this.#tenantId.set( '' );
                    this.#scopeTenant.set( null );
                    this.#pending$ = null;
                    return of( {} as ScopeTenant );   // schlägt nie fehl -> leerer ScopeTenant
                } ),
                shareReplay( { bufferSize: 1, refCount: false } )
            );

        return this.#pending$;
    }
}
